import React, { useState, useEffect } from "react"
import { Slider, Divider } from "@material-ui/core"
import { NumericInputEcomm, Button, TrackingEvent, Icon } from "@ncs/web-legos"
import { PartSearchResultsUrlState } from "../../PartSearchResults"
import { priceFilterStyles } from "./PriceRangeFilter.styles"

export const PRICE_FILTER_CONFIG = {
	MIN: 0,
	MAX: 1000,
	STEP: 5,
	DEFAULT_RANGE: [0, 1000] as [number, number],
}

export interface PriceRangeFilterProps {
	partQueryParams: PartSearchResultsUrlState
	setUrlState: (updater: (prev: PartSearchResultsUrlState) => PartSearchResultsUrlState) => void
	trackEvent: (event: TrackingEvent, data?: any) => void
}

export const PriceRangeFilter: React.FC<PriceRangeFilterProps> = ({
	partQueryParams,
	setUrlState,
	trackEvent,
}) => {
	const [sliderValue, setSliderValue] = useState<[number, number]>(
		PRICE_FILTER_CONFIG.DEFAULT_RANGE
	)

	const [minInput, setMinInput] = useState<number | undefined>(undefined)
	const [maxInput, setMaxInput] = useState<number | undefined>(undefined)

	useEffect(() => {
		const gte = partQueryParams.price_Gte
		const lt = partQueryParams.price_Lt

		const min = gte ? parseFloat(gte) : PRICE_FILTER_CONFIG.MIN
		const max = lt ? parseFloat(lt) : PRICE_FILTER_CONFIG.MAX

		const sliderMin = Math.min(Math.floor(min), PRICE_FILTER_CONFIG.MAX)
		const sliderMax = Math.min(Math.floor(max), PRICE_FILTER_CONFIG.MAX)
		setSliderValue([sliderMin, sliderMax])

		setMinInput(min === PRICE_FILTER_CONFIG.MIN ? undefined : min)
		setMaxInput(max === PRICE_FILTER_CONFIG.MAX ? undefined : max)
	}, [partQueryParams.price_Gte, partQueryParams.price_Lt])

	const handleSliderChangeImmediate = (_event: unknown, newValue: number | number[]) => {
		if (Array.isArray(newValue)) {
			setSliderValue(newValue as [number, number])
			const [min, max] = newValue

			setMinInput(min === PRICE_FILTER_CONFIG.MIN ? undefined : min)

			if (
				max === PRICE_FILTER_CONFIG.MAX &&
				maxInput &&
				maxInput > PRICE_FILTER_CONFIG.MAX
			) {
				return
			} else {
				setMaxInput(max === PRICE_FILTER_CONFIG.MAX ? undefined : max)
			}
		}
	}

	const handleSliderChangeCommitted = (_event: unknown, newValue: number | number[]) => {
		if (Array.isArray(newValue)) {
			const [min, max] = newValue

			setUrlState((prev) => ({
				...prev,
				price_Gte: min === PRICE_FILTER_CONFIG.MIN ? null : min.toString(),
				price_Lt: max === PRICE_FILTER_CONFIG.MAX ? null : max.toString(),
				page: 1,
			}))

			trackEvent(TrackingEvent.FILTERS, {
				id: "price-slider",
				min,
				max,
			})
		}
	}

	const handleApplyCustomRange = () => {
		const min = minInput ?? PRICE_FILTER_CONFIG.MIN
		const max = maxInput ?? PRICE_FILTER_CONFIG.MAX

		if (max !== PRICE_FILTER_CONFIG.MAX && min >= max) {
			return
		}

		if (min < PRICE_FILTER_CONFIG.MIN) {
			return
		}

		const sliderMin = Math.min(Math.floor(min), PRICE_FILTER_CONFIG.MAX)
		const sliderMax = Math.min(Math.floor(max), PRICE_FILTER_CONFIG.MAX)
		setSliderValue([sliderMin, sliderMax])

		setUrlState((prev) => ({
			...prev,
			price_Gte: min === PRICE_FILTER_CONFIG.MIN ? null : min.toString(),
			price_Lt: max === PRICE_FILTER_CONFIG.MAX ? null : max.toString(),
			page: 1,
		}))

		trackEvent(TrackingEvent.FILTERS, {
			id: "custom-price-range",
			min,
			max,
		})
	}

	return (
		<div css={priceFilterStyles.container}>
			<div css={priceFilterStyles.sliderContainer}>
				<Slider
					value={sliderValue}
					onChange={handleSliderChangeImmediate}
					onChangeCommitted={handleSliderChangeCommitted}
					min={PRICE_FILTER_CONFIG.MIN}
					max={PRICE_FILTER_CONFIG.MAX}
					step={PRICE_FILTER_CONFIG.STEP}
					valueLabelDisplay="auto"
					valueLabelFormat={(value) => `$${value}`}
					getAriaValueText={(value) => `$${value}`}
					getAriaLabel={() => "Price Range"}
				/>
				<div css={priceFilterStyles.sliderLabels}>
					<span>$0</span>
					<span>$1000+</span>
				</div>
			</div>

			<div css={priceFilterStyles.customRangeContainer}>
				<p css={priceFilterStyles.customRangeLabel}>Or enter custom range:</p>
				<div css={priceFilterStyles.inputsRow}>
					<NumericInputEcomm
						placeholder="$ Min"
						value={minInput}
						onChange={(value) => setMinInput(value)}
						min={0}
						allowNegative={false}
						decimalScale={2}
						css={{ marginBottom: 0 }}
						customInputStyle={priceFilterStyles.numericInput}
					/>
					<Divider
						orientation="horizontal"
						css={{ width: "12px", background: "#9CA3AF" }}
					/>
					<NumericInputEcomm
						placeholder="$ Max"
						value={maxInput}
						onChange={(value) => setMaxInput(value)}
						min={0}
						allowNegative={false}
						decimalScale={2}
						css={{ marginBottom: 0 }}
						customInputStyle={priceFilterStyles.numericInput}
					/>
					<Button
						onClick={handleApplyCustomRange}
						css={priceFilterStyles.applyButton}
						aria-label="Apply custom price range"
					>
						<Icon icon="arrow-right" color="white" />
					</Button>
				</div>
			</div>
		</div>
	)
}
